/**
 * API Client
 *
 * Axios instance configured for the Sentinel API with:
 * - Base URL configuration
 * - JWT token injection
 * - Error handling
 * - Response transformation
 *
 * Uses the auth store for token management.
 */
import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '../stores/auth'
import { notifyError } from './toastBridge'

// API base URL - uses Vite proxy in development
const API_BASE_URL = import.meta.env.DEV ? '/api' : 'https://api.claimshieldpro.com/api'

// Absolute URL for error reporting (native fetch, not axios)
const ERROR_REPORT_URL = import.meta.env.DEV
  ? '/api/v1/frontend-errors'
  : 'https://api.claimshieldpro.com/api/v1/frontend-errors'

/**
 * Silently report an API error to the backend for logging and Cliq alerts.
 * Uses native fetch() to avoid re-entering the axios interceptor.
 * Fire-and-forget — failures are swallowed.
 */
function reportErrorSilently(error: AxiosError): void {
  try {
    // Skip cancelled requests — these fire when a user navigates away or
    // swaps context (e.g. View As) before the previous call resolves.
    // Not actionable, pure noise in the alerts channel.
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return

    const url = error.config?.url || ''
    // Loop prevention: never report errors about the error-reporting endpoint
    if (url.includes('/frontend-errors')) return
    // Skip noisy statuses that aren't actionable
    const status = error.response?.status
    if (status === 400 || status === 401 || status === 410 || status === 422) return

    const authStore = useAuthStore()
    const errorData = error.response?.data as Record<string, unknown> | undefined
    const errorMessage =
      (errorData?.detail as string) || error.message || 'Unknown error'

    fetch(ERROR_REPORT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        error_message: String(errorMessage).slice(0, 2000),
        http_status: status ?? null,
        failed_endpoint: url || null,
        http_method: error.config?.method?.toUpperCase() || null,
        user_email: authStore.user?.email || null,
        page_path: window.location.pathname,
        timestamp: new Date().toISOString(),
      }),
      keepalive: true,
    })
    // No .then(), no .catch() — fire and forget
  } catch {
    // Swallow any synchronous errors (e.g. serialization)
  }
}

/**
 * Create and configure the Axios instance
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // 30 seconds
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request interceptor - add auth token
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Get token from auth store
      // Note: We access the store inside the interceptor to get fresh token
      const authStore = useAuthStore()

      if (authStore.token) {
        config.headers.Authorization = `Bearer ${authStore.token}`
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor - handle errors
  client.interceptors.response.use(
    (response) => {
      // Return data directly for cleaner usage
      return response
    },
    async (error: AxiosError) => {
      const originalRequest = error.config

      // Handle 401 Unauthorized
      if (error.response?.status === 401) {
        const authStore = useAuthStore()

        // Token expired or invalid - clear auth and redirect to login
        authStore.clearAuth()

        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`
        }

        return Promise.reject(error)
      }

      // Handle 403 Forbidden
      if (error.response?.status === 403) {
        console.error('[API] Forbidden - insufficient permissions')
      }

      // Handle 404 Not Found
      if (error.response?.status === 404) {
        console.error('[API] Resource not found:', originalRequest?.url)
      }

      // Handle 422 Validation Error - log full details for debugging
      if (error.response?.status === 422) {
        console.error('[API] Validation error:', originalRequest?.method?.toUpperCase(), originalRequest?.url, error.response.data)
      }

      // Handle 500+ Server errors
      if (error.response?.status && error.response.status >= 500) {
        console.error('[API] Server error:', error.response.data)
      }

      // Handle network errors
      if (!error.response) {
        console.error('[API] Network error - server may be unreachable')
      }

      // User-facing toast for genuine failures.
      // Skip on: cancellations, auth flows, view-specific statuses, and timeouts
      // during initial page load (where the view's own error state is better UX).
      const isCancelled = axios.isCancel(error) || error.code === 'ERR_CANCELED'
      const status = error.response?.status
      const isNetwork = !error.response
      const isTimeout = error.code === 'ECONNABORTED'

      if (!isCancelled) {
        if (status && status >= 500) {
          notifyError(
            'Something went wrong',
            "We hit an error on our end. Please try again — if it keeps happening, let us know.",
          )
        } else if (isTimeout) {
          notifyError(
            'Request timed out',
            'The server took too long to respond. Check your connection and try again.',
          )
        } else if (isNetwork) {
          notifyError(
            'Connection problem',
            "We couldn't reach the server. Check your internet connection and try again.",
          )
        }
      }

      // Silent background reporting (native fetch, fire-and-forget)
      reportErrorSilently(error)

      return Promise.reject(error)
    }
  )

  return client
}

// Export singleton instance
export const api = createApiClient()

/**
 * Type-safe API helpers
 */
export const apiHelpers = {
  /**
   * GET request with typed response
   */
  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await api.get<T>(url, { params })
    return response.data
  },

  /**
   * POST request with typed response
   */
  async post<T>(url: string, data?: unknown): Promise<T> {
    const response = await api.post<T>(url, data)
    return response.data
  },

  /**
   * PUT request with typed response
   */
  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await api.put<T>(url, data)
    return response.data
  },

  /**
   * PATCH request with typed response
   */
  async patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await api.patch<T>(url, data)
    return response.data
  },

  /**
   * DELETE request with typed response
   */
  async delete<T>(url: string): Promise<T> {
    const response = await api.delete<T>(url)
    return response.data
  },
}

/**
 * Error type for API errors
 */
export interface ApiError {
  detail: string | Array<{ loc: string[]; msg: string; type: string }>
  status_code?: number
  errors?: Record<string, string[]>
}

/**
 * Extract error message from API error
 */
export function getApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    // No response at all — server unreachable, CORS, timeout, or rebuild window.
    // Surface a human-friendly hint instead of axios's raw "Network Error".
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        return 'Request timed out — the server took too long to respond. Try again in a moment.'
      }
      return 'Server is temporarily unavailable. This usually clears in 30 seconds — try again.'
    }

    const data = error.response.data as (ApiError & { error?: { message?: string; code?: string } }) | undefined

    // Sentinel APIResponse.fail shape: { success: false, error: { code, message, ... } }
    // Prefer this over the FastAPI `detail` fallback so the user sees the
    // handler's own wording instead of a raw status line.
    if (data?.error?.message) {
      return data.error.message
    }

    // FastAPI detail message
    if (data?.detail) {
      // Handle 422 validation errors (detail is an array of objects)
      if (Array.isArray(data.detail)) {
        return data.detail
          .map((e) => `${e.loc?.join('.') || 'field'}: ${e.msg}`)
          .join('; ')
      }
      return data.detail
    }

    // HTTP status text
    if (error.response.statusText) {
      return error.response.statusText
    }

    // Fallback
    if (error.message) {
      return error.message
    }
  }

  // Plain Error (e.g. thrown by service layer when API returns success=false)
  if (error instanceof Error && error.message) {
    return error.message
  }

  // Unknown error
  return 'An unexpected error occurred'
}

/**
 * Driver-facing error messaging helpers.
 *
 * Used in public driver flows (apply, sign, dqf-completion, employer-contact)
 * where the audience is a non-technical CDL driver on a phone, not a staff
 * member who can read a stack trace.
 *
 * Two entry points so it works for both axios- and fetch-based callers:
 *
 *   - getDriverFriendlyErrorMessage(error, opts)   — pass an AxiosError, Error,
 *     or anything getApiErrorMessage can interpret.
 *   - mapBackendDetailToFriendly(detail, status, opts) — pass the raw
 *     `json.detail` string + HTTP status when you already parsed the
 *     fetch Response yourself.
 *
 * Both share the same mapping table. The mapping is defense-in-depth: the
 * backend should already send a friendly `detail`, but if a stale deploy or
 * an unhandled 500 slips through, this layer still produces something the
 * driver can act on.
 *
 * Pass `{ contactHint: 'employer' | 'support' }` to control whether the
 * fallback tells the driver to contact their employer (signing flows) or
 * support@claimshieldpro.com (apply flow).
 */
export interface DriverFriendlyOpts {
  contactHint?: 'employer' | 'support'
}

function _contactLine(opts: DriverFriendlyOpts): string {
  return opts.contactHint === 'employer'
    ? "If this keeps happening, ask your employer to resend the link."
    : "If this keeps happening, email support@claimshieldpro.com."
}

/**
 * Map a backend `detail` string (and optional status) to a driver-friendly
 * message. Used directly by fetch-based views that parsed the response
 * themselves; also called internally by getDriverFriendlyErrorMessage.
 */
export function mapBackendDetailToFriendly(
  detail: string | null | undefined,
  status: number | undefined,
  opts: DriverFriendlyOpts = {},
): string {
  const contactLine = _contactLine(opts)
  const genericFallback = `Something went wrong. Please try again. ${contactLine}`

  // Missing detail on a 5xx → never expose the raw status code to a driver.
  if (!detail || !detail.trim()) {
    if (status && status >= 500) return genericFallback
    if (status === 404) return 'We couldn\'t find what you were looking for. Please double-check the link from your employer.'
    if (status === 410) return 'This link has expired or has already been used. Please ask your employer for a new one.'
    return genericFallback
  }

  const lower = detail.toLowerCase()

  // Generic placeholders or stale vague messages.
  if (
    lower === 'an error occurred' ||
    lower === 'internal server error' ||
    lower === 'an unexpected error occurred' ||
    lower.startsWith('failed to submit signature') ||
    lower.startsWith('failed to get form content') ||
    lower.startsWith('failed to create authorization bundle') ||
    lower.startsWith('failed to load')
  ) {
    return genericFallback
  }

  // Internal jargon that occasionally leaked from older code paths.
  if (
    lower.includes('cannot route') ||
    lower.includes('no linked driver record') ||
    lower.includes('no linked employee record')
  ) {
    return `We're having trouble saving that. Please try again. ${contactLine}`
  }
  if (lower.startsWith("unsupported document_type")) {
    return detail.replace(/^Unsupported document_type '[^']*'\.\s*/i, "That document type isn't supported here. ")
  }
  if (lower.startsWith('bundle not found or expired')) {
    return 'This signing link has expired or is no longer valid. Please ask your employer to send a new one.'
  }

  return detail
}

export function getDriverFriendlyErrorMessage(
  error: unknown,
  opts: DriverFriendlyOpts = {},
): string {
  // Pull status off the axios error directly when possible so we can apply
  // the status-aware fallback in mapBackendDetailToFriendly.
  let status: number | undefined
  if (axios.isAxiosError(error)) {
    status = error.response?.status
    // No response at all — getApiErrorMessage already returns a friendly
    // network message; just defer to it.
    if (!error.response) {
      return getApiErrorMessage(error)
    }
  }
  const baseMsg = getApiErrorMessage(error)
  return mapBackendDetailToFriendly(baseMsg, status, opts)
}

// Build marker — referenced below so the bundler keeps it, which forces a
// new content-hash on the shared services chunk and gets CF Pages' edge cache
// to stop serving the prior chunk's stale 404 SPA fallback (observed
// 2026-04-22 after the APIResponse.fail → 4xx rollout).
const __API_HELPER_BUILD = '2026-04-22T21:22-apiresponse-fail-4xx'
if (typeof window !== 'undefined') {
  ;(window as unknown as { __API_HELPER_BUILD?: string }).__API_HELPER_BUILD = __API_HELPER_BUILD
}

